import { action } from '@/lib/api.js';
import { updateAiSuggestion } from '@/redux/actions/gravityActions.js';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors.js';
import { actionArgumentListSuggestions, aiSuggestionsByPath, isSuggestionsLoading } from '@/redux/selectors/aiSelectors.js';
import appActions from '@/screens/apps/redux/actions/appActions.js';
import * as Popover from '@radix-ui/react-popover';
import { activeAppSchemas } from '@shared/schema/aiSchemas/active/appSchemas/activeAppSchemas.js';
import { matchPathWithTemplate } from '@shared/utils/matchPathWithTemplate.js';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import AiSuggestionsContent from './AiSuggestionsContent.jsx';
import appScreenSelectors from '@/screens/apps/redux/selectors/appScreenSelectors.js';
import { randomId } from '@shared/utils/randomId.js';
import formatArgumentKey from '@/lib/formatArgumentKey.js';

const AiSuggestionsPopover = ({ children, side = 'bottom', sideOffset = 4, align = 'middle', showDiff = true, path, showArgumentsSuggestions = false }) => {
  const [open, setOpen] = useState(false);
  const isLoading = useSelector(isSuggestionsLoading);
  const suggestionsByPath = useArgSelector(aiSuggestionsByPath, path) || [];
  const argumentSuggestions = useArgSelector(actionArgumentListSuggestions);
  const actionId = useSelector((state) => appScreenSelectors.activeAppAction(state)?.id);
  const docUrl = useSelector((state) => appScreenSelectors.activeAppAction(state)?.documentation?.url);

  const mappedSuggestions = useMemo(() => {
    const mapped = suggestionsByPath?.map((n) => {
      const events = activeAppSchemas?.events;
      const matchingEvent = events?.find((event) => matchPathWithTemplate(n?.path, event?.path));
      return {
        ...n,
        key: n?.path,
        title: matchingEvent?.label,
        info: matchingEvent?.description
      };
    });

    if (!showArgumentsSuggestions) {
      return mapped;
    }

    return [
      { title: 'General', items: mapped },
      { title: 'Arguments', items: argumentSuggestions?.map((n) => ({ ...n, title: n?.label || n?.key, suggestionType: 'arguments' })) }
    ];
  }, [JSON.stringify(suggestionsByPath), JSON.stringify(argumentSuggestions)]);

  const handleOpenChange = (open) => setOpen(open);

  const handleAction = (suggestion, actionName) => {
    updateAiSuggestion({ id: suggestion?.id, path: suggestion?.path, action: actionName });
    action('REMOVE_AI_SUGGESTION', { id: suggestion?.id });

    if (actionName === 'accept') {
      appActions.set(suggestion?.path, suggestion?.suggestion?.suggestedValue);
    }
  };

  const removeArgumentFromSuggestion = (suggestion) => {
    action('REMOVE_FROM_ARGUMENT_LIST_SUGGESTIONS', { key: suggestion.key, actionId });
  };

  const handleMultiAccept = async (items) => {
    for (const item of items) {
      if (item?.suggestionType === 'arguments') {
        const payload = {
          id: randomId(),
          location: item?.location || 'body',
          key: formatArgumentKey(item?.key, { isArray: item?.dataType === 'array' }),
          externalKey: formatArgumentKey(item?.externalKey, { isArray: item?.dataType === 'array' }),
          required: false,
          name: item?.label,
          dataType: item?.dataType || 'string',
          hidden: false
        };
        await appActions.merge(`actions[id:${actionId}].arguments`, 'id', payload);
        removeArgumentFromSuggestion(item);
      } else {
        handleAction(item, 'accept');
      }
    }
    setOpen(false);
  };

  const emptyStateMessage = path ? 'No new suggestions for this field.' : 'No new suggestions.';

  return (
    <Popover.Root open={open} onOpenChange={handleOpenChange}>
      <Popover.Trigger asChild>{children}</Popover.Trigger>
      <AiSuggestionsContent //
        title={`AI Suggestion${mappedSuggestions?.length > 1 || mappedSuggestions?.length === 0 ? 's' : ''}`}
        isLoading={isLoading}
        items={mappedSuggestions}
        emptyStateMessage={emptyStateMessage}
        side={side}
        showDiff={showDiff}
        sideOffset={sideOffset}
        align={align}
        onStop={(n) => handleAction(n, 'stop')}
        onReject={(n) => handleAction(n, 'reject')}
        onAccept={(n) => handleAction(n, 'accept')}
        onMultiAccept={handleMultiAccept}
        multiSelect={showArgumentsSuggestions}
        buttonLabel={'Apply'}
        headerDocs={showArgumentsSuggestions ? docUrl : null}
      />
    </Popover.Root>
  );
};

export default AiSuggestionsPopover;
